import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonButtonRowWidget, CommonWistiaPlayerWidget, WistiaService } from '@eforall/common';
import { DialogService, DroplistItem, DroplistWidget } from '@eforall/dashboard';
import { BulkChangeType, TrimesterWindowEnum } from '@interfaces';
import { FuncService, TrimesterService } from '../../services';
import { TrimesterDroplistWidget, VenueDialog } from '../../ui/widget';
import { RushDialog } from './dialogs/rush.dialog';
import { SizedDialog } from './dialogs/sized.dialog';


@Component({
	selector: 'tech-page',
	standalone: true,
	imports: [
		CommonButtonRowWidget,
		TrimesterDroplistWidget,
		CommonWistiaPlayerWidget,
		DroplistWidget
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './tech.page.html'
})
export class TechPage {

	private func = inject(FuncService);
	private dialogService = inject(DialogService);
	private trimesterService = inject(TrimesterService);
	public wistiaService = inject(WistiaService);

	selectedYear: number | undefined = undefined;

	private nextId = 1;
	private bulking = false;
	public message = signal('');
	public lmiMessage = signal('');
	public countOfMissingHudData = signal(undefined as number | undefined);
	public loaded = signal(false);
	public updateVideoClicked = signal(false);
	public getVideoClicked = signal(false);
	public readonly trimesterWindow = TrimesterWindowEnum.Programming;
	public selectedTrimester = computed(() => this.trimesterService.selectedTrimester());
	public oldDataMigrationMessage = signal('');

	public years: DroplistItem<never, number>[] = [2026, 2025, 2024, 2023, 2022, 2021, 2020].map((year, i) => ({
		uniqueId: year,
		iconContext: {
			iconClass: 'fa-calendar-range',
			iconStyle: 'fa-solid',
			text: year.toString(),
			hideText: 'never',
		},
	}));

	public videoActivities = signal<{
		courseVideoActivityId: number;
		timeInSeconds: number;
		wistiaId: string;
		couserVersionIds: string,
		title: string,
		apiTimeInSeconds: number;
	}[]>([]);

	public async getWistiaVideoActivities() {
		this.getVideoClicked.set(true);
		const videoActivities = await this.func.tech.getVideoActivities();

		const apiActivities: { [wistiaId: string]: number } = {};

		for (const va of videoActivities) {
			await this.wistiaService.replaceVideo(va.wistiaId);
			const timeInSeconds = await this.wistiaService.getTimeInSeconds() || 0;
			apiActivities[va.wistiaId] = Math.round(timeInSeconds);
		}


		const activities = videoActivities.reduce((a: {
			courseVideoActivityId: number;
			timeInSeconds: number;
			wistiaId: string;
			couserVersionIds: string,
			title: string,
			apiTimeInSeconds: number;
		}[], v) => {
			const apiTimeInSeconds = apiActivities[v.wistiaId]!;
			if (v.timeInSeconds != apiTimeInSeconds) a.push({
				apiTimeInSeconds,
				courseVideoActivityId: v.courseVideoActivityId,
				timeInSeconds: v.timeInSeconds,
				couserVersionIds: v.courseVersionIds.join(', '),
				title: v.title,
				wistiaId: v.wistiaId,
			});
			return a;
		}, []);

		this.videoActivities.set(activities);
		this.loaded.set(true);
	}

	public setYear(year: number | undefined) {
		this.selectedYear = year ?? undefined;
		this.countOfMissingHudData.set(undefined);
	}


	public async updateActivitiesTimeInSeconds(courseVideoActivityId: number, timeInSeconds: number) {
		if (!this.videoActivities().length) return;

		const result = await this.dialogService.confirm('Are you sure you want to update the video activity timeInSeconds with the API timeInSeconds?');

		if (result) {
			this.updateVideoClicked.set(true);

			await this.func.tech.setVideoActivityTimeInSeconds({ courseVideoActivityId, timeInSeconds });

			this.videoActivities.update(activities => {
				const activity = activities.find(atv => atv.courseVideoActivityId == courseVideoActivityId);
				if (!activity) return activities;
				activity.timeInSeconds = timeInSeconds;
				return activities;
			});

			this.updateVideoClicked.set(false);
		}

	}

	public async setAccByApplication() {
		await this.func.jobs.setAccByApplication({ trimesterId: this.selectedTrimester()?.trimesterId })
	}

	public async setAccByLead() {
		await this.func.jobs.setAccByLead({ trimesterId: this.selectedTrimester()?.trimesterId })

	}

	public async setCommitmentByApplication() {
		await this.func.jobs.setCommitmentByApplication({ trimesterId: this.selectedTrimester()?.trimesterId })

	}

	public async setCommitmentByLead() {
		await this.func.jobs.setCommitmentByLead({ trimesterId: this.selectedTrimester()?.trimesterId })

	}

	public async refreshAccProspects() {
		await this.func.jobs.acc_prospect.refreshAccProspects({ trimesterId: this.selectedTrimester()?.trimesterId });
	}

	public async refreshAccProspectsForAllTrimesters() {
		await this.func.jobs.acc_prospect.refreshAccProspectsForAllTrimesters({});
	}

	public async getMissingHUDDataCount() {
		// await this.func.jobs.setCommitmentByLead({ trimesterId: this.selectedTrimester()?.trimesterId })
		if (!this.selectedYear) {
			this.lmiMessage.set('Please select a year first.');
			return;
		}
		else {
			const count = await this.func.tech.getMissingHudDataCount({ year: this.selectedYear });
			this.countOfMissingHudData.set(count);
			return;
		}
	}

	public async regenerateLMIData() {
		if (!this.selectedYear) {
			this.lmiMessage.set('Please select a year first.');
		}
		else await this.func.jobs.setMissingLmiDataForYear({ year: this.selectedYear });
		this.countOfMissingHudData.set(undefined);
	}

	public async bulkUpdateAccApplicationFilterableValues() {
		await this.startBulk('acc_application');
	}

	public async bulkUpdateAccLeadFilterableValues() {
		await this.startBulk('acc_lead');
	}

	async startBulk(bulkType: BulkChangeType) {
		this.nextId = 1;
		this.doBulk(bulkType);
	}

	async doBulk(bulkType: BulkChangeType) {

		if (this.bulking) return;
		this.bulking = true;
		this.message.set('');

		const r = await this.func.bulkUpdate.bulkUpdate({ bulkType, nextId: this.nextId });
		if (r.nextId <= r.maxId) {
			this.nextId = r.nextId;
			this.bulking = false;
			this.doBulk(bulkType);
		}
		else {
			this.message.set('Bulk update complete for ' + bulkType);
			this.nextId = 1;
			this.bulking = false;
		}
	}


	async showDialog(dialogType: 'Confirm' | 'LongText' | 'Custom' | 'Custom-Sized') {

		if (dialogType == 'Confirm') {
			const result = await this.dialogService.confirm('Confirm that you want to do something...', 'Yes', 'No', [], 150, 150);
			this.dialogService.showMessage('Result', result ? 'You said, "Yes."' : 'You said, "No."');
		}

		if (dialogType == 'LongText') {
			this.dialogService.showMessage('Lorem Ipsum',
				`Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum. Curabitur pretium tincidunt lacus. Nulla gravida orci a odio. Nullam varius, turpis et commodo pharetra, est eros bibendum elit, nec luctus magna felis sollicitudin mauris. Integer in mauris eu nibh euismod gravida. Duis ac tellus et risus vulputate vehicula. Donec lobortis risus a elit. Etiam tempor. Ut ullamcorper, ligula eu tempor congue, eros est euismod turpis, id tincidunt sapien risus a quam. Maecenas fermentum consequat mi. Donec fermentum. Pellentesque malesuada nulla a mi. Duis sapien sem, aliquet nec, commodo eget, consequat quis, neque.

Aliquam faucibus, elit ut dictum aliquet, felis nisl adipiscing sapien, sed malesuada diam lacus eget erat. Cras mollis scelerisque nunc. Nullam arcu. Aliquam consequat. Curabitur augue lorem, dapibus quis, laoreet et, pretium ac, nisi. Aenean magna nisl, mollis quis, molestie eu, feugiat in, orci. In hac habitasse platea dictumst. Fusce convallis, mauris imperdiet gravida bibendum, nisl turpis suscipit mauris, sed placerat ipsum urna sed risus. In convallis tellus a mauris. Curabitur non elit ut libero tristique sodales. Mauris a lacus. Donec mattis semper leo. In hac habitasse platea dictumst. Vivamus facilisis diam at odio. Parin et mauris vel nunc suscipit mollis. Aliquam et elit eu nunc rhoncus sagittis.

Morbi tincidunt, dui sit amet facilisis feugiat, odio metus gravida ante, ut pharetra massa metus id nunc. Duis scelerisque molestie turpis. Sed fringilla, massa eget luctus malesuada, metus eros molestie lectus, ut tempus eros massa ut dolor. Aenean aliquet fringilla sem. Suspendisse sed ligula in ligula suscipit aliquam. Praesent in eros vestibulum mi adipiscing adipiscing. Morbi facilisis. Curabitur ornare consequat nunc. Aenean vel metus. Ut posuere viverra nulla. Aliquam erat volutpat. Pellentesque convallis. Maecenas feugiat, tellus pellentesque pretium posuere, felis lorem euismod felis, eu ornare leo nisi vel felis. Mauris consectetur tortor et ipsum.

Vestibulum volutpat pretium libero. Cras id dui. Aenean ut eros et nisl sagittis vestibulum. Nullam nulla eros, ultricies sit amet, nonummy id, imperdiet feugiat, pede. Sed lectus. Donec mollis hendrerit risus. Phasellus nec sem in justo pellentesque facilisis. Etiam imperdiet imperdiet orci. Nunc nec neque. Phasellus leo dolor, tempus non, auctor et, hendrerit quis, nisi. Curabitur ligula sapien, tincidunt non, euismod vitae, posuere imperdiet, leo. Maecenas malesuada. Praesent congue erat at massa. Sed cursus turpis vitae tortor. Donec posuere vulputate arcu. Phasellus accumsan cursus velit. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia Curae; Sed aliquam, nisi quis porttitor congue, elit erat euismod orci, ac placerat dolor lectus quis orci.

Phasellus consectetuer vestibulum elit. Amet justo. Morbi ut odio. Suspendisse potenti. Morbi mattis felis at nunc. Duis viverra diam non justo. In nisl. Nullam sit amet magna in magna gravida vehicula. Mauris tincidunt sem sed arcu. Nunc posuere. Nullam lectus justo, vulputate eget, mollis sed, tempor sed, magna. Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Etiam neque. Sed elit. Curabitur ac leo arcu. Sed rhoncus.`, 'Ok', [], 900, 700);
		}


		if (dialogType == 'Custom') {
			await this.dialogService.showCustom(
				RushDialog,
				{ data: { rush: 'Amazing' } },
				700, 700);
		}

		if (dialogType == 'Custom-Sized') {
			await this.dialogService.showCustom(
				SizedDialog,
				{ data: {} },
				700, 700);
		}


	}

	public async showVenueDialog(venueId: number) {
		await this.dialogService.showCustom(VenueDialog, { data: {} }, 900, 700);
	}

	public async updateAccAppSwitchoverStatusId() {
		await this.func.tech.updateAccAppSwitchoverStatusId({ trimesterId: 261 });
	}


	public async testTableHelpers() {

		await this.func.tech.testTableHelpers();
	}

	public async migrateData() {

		const message = await this.func.tech.migrateOldTables();
		this.oldDataMigrationMessage.set(message);
	}

	public async migrateDropoutAndAddressData() {

		const message = await this.func.tech.addDropoutAndAddressData();
	}


}
